// Event token 1 is the Gallery Token.
export const GALLERY_TOKEN_ID = "1";
// A solution this fresh reads "Solved just now" instead of an age.
const GALLERY_JUST_NOW_MS = 5 * 60_000;
// How often the backend's progress is read while a solve run is going.
export const GALLERY_POLL_MS = 400;
/**
 * "461K", "1.2M": Intl's en-US compact notation, by hand, because Hermes
 * ignores `notation: "compact"` and prints the full number.
 */
const COMPACT_UNITS = [
    [1, ""],
    [1e3, "K"],
    [1e6, "M"],
    [1e9, "B"],
];
export const compact = {
    format: (n) => {
        const abs = Math.abs(n);
        // Rounded per unit, then up a unit when it reaches 1000: 999,500 is "1M", not "1000K".
        for (const [i, [div, suffix]] of COMPACT_UNITS.entries()) {
            const next = COMPACT_UNITS[i + 1];
            if (next && abs >= next[0])
                continue;
            const scaled = abs / div;
            // One decimal below 10, like Intl's two significant digits.
            const rounded = scaled < 10 && div > 1
                ? Math.round(scaled * 10) / 10
                : Math.round(scaled);
            if (next && rounded >= 1000)
                continue;
            return `${n < 0 ? "-" : ""}${rounded}${suffix}`;
        }
        return String(n);
    },
};
export const grouped = new Intl.NumberFormat("en-US");
/**
 * The card's completed tick: one press completes the selected grade (and every
 * grade below); on a grade already completed it unmarks all. Nothing is
 * selected when no grade is (`selectedIndex` -1 or undefined).
 */
export const completedGradeToggle = (grades, completedIndex, selectedIndex) => {
    const selected = selectedIndex !== undefined && selectedIndex >= 0
        ? grades[selectedIndex]
        : undefined;
    const completed = !!selected && (selectedIndex ?? -1) <= completedIndex;
    return {
        selected,
        completed,
        /** What a press saves: null unmarks, a grade completes it. */
        next: completed ? null : (selected?.name ?? null),
    };
};
/** Tokens are cumulative: a grade pays its own and every grade's below it. */
export const tokensUpTo = (grades, i) => grades
    .slice(0, i + 1)
    .reduce((sum, g) => sum + (g.tokenRewards[GALLERY_TOKEN_ID] ?? 0), 0);
/** EasySBC's verdict on a grade or plan from its tax loss per Gallery Token gained. */
export const galleryVerdict = (coinsPerToken) => coinsPerToken < 600 ? "good" : coinsPerToken <= 1800 ? "fair" : "bad";
/** Every grade up to this index is completed; -1 for none. */
export const completedIndexOf = (grades, completedGrade) => grades.findIndex((g) => g.name === completedGrade);
/** A solve of the set is queued or running in the backend. */
export const isSolving = (status) => status === "queued" || status === "solving";
/** Why a grade can't be picked, as a `gallery` i18n key; undefined when it can. */
export const gradeHintKey = (grade, completed) => completed || !grade
    ? undefined
    : !grade.reachable
        ? "list.notReachable"
        : !grade.onMarket
            ? "list.notOnMarket"
            : undefined;
/**
 * What a set card shows for its selected grade: the user's pick (unreachable
 * ones too, to see their tokens), else the sort's pick, else the highest
 * reachable on-market grade, else the completed grade.
 */
export const describeGalleryCard = ({ grades, cardGrades, completedGrade, hasSolution, pickedIndex, sortPick, }) => {
    const completedIndex = completedIndexOf(grades, completedGrade);
    // No grade above the completed one has a solution: dimmed, no stats.
    const unsolvable = cardGrades.length > 0 &&
        completedIndex < grades.length - 1 &&
        !cardGrades.some((g, i) => i > completedIndex && g.reachable);
    const highestOnMarket = cardGrades.findLastIndex((g) => g.reachable && g.onMarket);
    const selectedIndex = pickedIndex ??
        sortPick ??
        (unsolvable
            ? completedIndex + 1
            : // No grade the market sells (e.g. objective players only): a completed set shows its completed grade.
                highestOnMarket === -1 && completedIndex >= 0
                    ? completedIndex
                    : highestOnMarket);
    const selected = cardGrades[selectedIndex];
    const tokens = tokensUpTo(grades, selectedIndex);
    // Tax loss per token gained over the completed grades.
    const tokensGained = tokens - tokensUpTo(grades, completedIndex);
    // No tokens gained is always bad, even with nothing to buy. Completed grades get none.
    const verdict = !selected?.reachable || selectedIndex <= completedIndex
        ? undefined
        : tokensGained > 0
            ? galleryVerdict(selected.taxLoss / tokensGained)
            : "bad";
    // An unreachable grade keeps the bar, greyed at 0.
    const showCollected = hasSolution && !!selected;
    const nothingToBuy = showCollected &&
        !!selected?.reachable &&
        selected.collectedCount === selected.size;
    return {
        completedIndex,
        unsolvable,
        selectedIndex,
        selected,
        tokens,
        tokensGained,
        verdict,
        showCollected,
        nothingToBuy,
        /** The grade shown is one already completed in game. */
        dimmed: selectedIndex >= 0 && selectedIndex <= completedIndex,
    };
};
/**
 * The set page's grade: the picked one while selectable, else the highest
 * reachable on-market one, else the highest reachable one.
 */
export const solutionSelectedIndex = (grades, pickedGrade, completedIndex) => {
    const pickedIndex = grades.findIndex((g) => g.grade === pickedGrade);
    const picked = grades[pickedIndex];
    if (picked && (picked.reachable || pickedIndex <= completedIndex))
        return pickedIndex;
    return [
        grades.findLastIndex((g) => g.reachable && g.onMarket),
        grades.findLastIndex((g) => g.reachable),
        grades.length - 1,
    ].find((i) => i >= 0);
};
/** "Solved just now" or the solve's age in ms. */
export const solvedAge = (computedAt, now = Date.now()) => {
    const age = now - Date.parse(computedAt);
    return age < GALLERY_JUST_NOW_MS ? "justNow" : age;
};
//# sourceMappingURL=grades.js.map